import { type OutlookEvent } from "@/features/calendar/types";
import { getIcon, getRenderTypeInfo } from "@/features/calendar/utils";
import linkOutIcon from "@ingka/ssr-icon/paths/link-out";
import { Button, LightMode, Text } from "@repo/skapa";
import { T, t } from "@repo/translation";
import { LocalizedTimeRange, translateResponseStatus } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

import {
  ActionsCell,
  ActionsCellContent,
  DetailsCell,
  InfoCell,
  ItemIcon,
  TimeCell,
  TimeCellContent,
  TwoLineClamp,
} from "./components";

export const OutlookCells = ({
  event,
  backgroundColor,
}: {
  event: OutlookEvent;
  backgroundColor?: string;
}) => {
  const startDate = new Date(`${event.start.dateTime}+00:00`);
  const endDate = new Date(`${event.end.dateTime}+00:00`);
  const textColor = event.isCancelled
    ? LightMode.colourTextAndIcon3
    : LightMode.colourTextAndIcon1;

  return (
    <>
      <TimeCell backgroundColor={backgroundColor}>
        <TimeCellContent>
          <ItemIcon aria-hidden paths={getIcon(getRenderTypeInfo(event))} />
          <Text
            textStyle="Body.S"
            lineThrough={event.isCancelled}
            color={textColor}
          >
            {event.isAllDay ? (
              <T translationKey="label.all_day" />
            ) : (
              <LocalizedTimeRange fromDate={startDate} toDate={endDate} />
            )}
          </Text>
        </TimeCellContent>
      </TimeCell>
      <DetailsCell>
        <Text
          textStyle="Heading.XS"
          lineThrough={event.isCancelled}
          color={textColor}
          overflowHidden
        >
          {event.subject}
        </Text>
        {event.location?.displayName && (
          <Text textStyle="Body.S" overflowHidden>
            {event.location.displayName}
          </Text>
        )}
      </DetailsCell>
      <InfoCell>
        {event.attendees && (
          <Text textStyle="Body.S" color={textColor} overflowHidden>
            {t("label.participants", { count: event.attendees.length })}
          </Text>
        )}
        <Text textStyle="Body.S" color={textColor} overflowHidden>
          {translateResponseStatus(event.responseStatus?.response)}
        </Text>
        {event.sensitivity === "private" && (
          <Text textStyle="Body.S" color={textColor} overflowHidden>
            {t("label.private")}
          </Text>
        )}
      </InfoCell>
      <InfoCell wide>
        {event.bodyPreview && (
          <TwoLineClamp>
            <Text textStyle="Body.S">{event.bodyPreview}</Text>
          </TwoLineClamp>
        )}
      </InfoCell>
      <InfoCell wide />
      <ActionsCell>
        <ActionsCellContent>
          {event.onlineMeeting?.joinUrl && (
            <Button
              size="small"
              iconOnly
              type="primary"
              ssrIcon={linkOutIcon}
              text={t("action.join_teams_meeting")}
              title={t("action.join_teams_meeting")}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(
                  event.onlineMeeting?.joinUrl,
                  "_blank",
                  "noopener,noreferrer"
                );
                trackAnalytics("join_teams_meeting");
              }}
            />
          )}
        </ActionsCellContent>
      </ActionsCell>
    </>
  );
};
